import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { frozenRateFields } from './fx.js';
import { pagedQuery, pagedResponse } from './pagination.js';
import { CurrencySchema, IsoDateSchema, MessageSchema, MoneyStringSchema, } from './primitives.js';
import { ReceiptTenderMethodSchema, ReceiptTenderSchema, ReceiptTendersInputSchema, TENDER_MESSAGES, tenderSummaryFields, } from './tenders.js';
const c = initContract();
/**
 * Receipt Vouchers (PART 11, FIN-03) — money in from a customer, spread
 * across the invoices it settles, or other income to a named account.
 *
 * Permission area: `accounting` — FIN-03 §9: "Reaching this screen requires
 * the accounting-area permission", and 16.2.2 row 6 lists vouchers under
 * `accounting`. Per action: read = accounting.view · create/edit a draft and
 * cancel a draft = accounting.create/edit (the spec's "receipt-voucher write
 * permission") · post = accounting.approve (the "receipt-voucher post
 * permission") · delete a draft = accounting.delete.
 *
 * Allocation rules (the screen's real work):
 *  - BR-FIN-03-06/-07: neither one row above its invoice's balance nor the
 *    total above the amount received, both within HALF A CENT (N-13). The
 *    server refuses an over-allocated write outright; the prototype's red
 *    strip is a client-side drawing of the same rule.
 *  - BR-FIN-03-09: money left over is customer credit — shown, never a block.
 *  - BR-FIN-03-10: auto-allocate walks OLDEST FIRST; with a zero amount it
 *    fills every invoice and writes the total back into the amount.
 *  - R9: an invoice's "already paid" counts POSTED receipts only.
 *
 * Lifecycle per R8 / BR-FIN-03-16/-17: drafts freely editable and deletable;
 * Draft → Posted and Draft → Cancelled are the ONLY transitions; a posted
 * voucher is read-only in EVERY field and is corrected by the `reverse`
 * route: the ledger flips the voucher's own entry line by line (the frozen
 * exchange difference with it — GEN-113 undone by re-reading, never by
 * recomputing), the reversed_at stamp releases the allocations and restores
 * every invoice's balance, and the two documents point at each other. Reason
 * required and audited. The reversal is dated the company's TODAY (GEN-73 —
 * which is what lets it post into an open period when the original's month
 * has closed), and a reversal cannot itself be reversed (FIN-02 §15).
 */
// ── The fixed sentences of FIN-03 ───────────────────────────────────────────
export const RECEIPT_MESSAGES = {
    /**
     * The red strip (BR-FIN-03-06/-07/-08) — one sentence for all three of
     * ERR-FIN-03-01 (over an invoice's balance), ERR-FIN-03-02 (over the money
     * received) and ERR-FIN-03-03 (posting while over-allocated).
     *
     * ERR-SAL-00-05 — "entered payments above the grand total" — is this same
     * refusal, relocated: SAL-00 §4's payment card became the FIN-03 Receipt,
     * so the act that row describes is performed here, as an allocation above
     * the invoice's balance due, and this is the sentence the user gets. TWO
     * RECORDED DEVIATIONS from the prototype's row, both deliberate: the
     * wording is FIN-03's own (the prototype's sentence quoted its document's
     * figures; this one is a fixed strip shown live while typing), and the
     * row's second remedy — "record the extra as an advance" — is not a
     * refusal path here at all but the model's default: money received above
     * what is allocated simply stays customer credit (BR-FIN-03-09), so the
     * advance the prototype told the user to go and record is what the build
     * records by itself.
     */
    overAllocated: 'Over-allocated — amount applied exceeds received or invoice balance',
    /** The posted locked notice — ERR-FIN-03-08's read-only fields, with the reason. */
    postedLocked: 'This receipt is posted and locked. A journal entry was generated and the customer balance updated.',
    /** The cancelled locked notice. */
    cancelledLocked: 'This receipt is cancelled and read-only.',
    /** GEN-59: the cancelling act carries its reason. */
    cancelReasonRequired: 'A receipt can only be cancelled with a reason, which is stored on the voucher and written to the audit log.',
    /** BR-FIN-03-01 / ERR-FIN-03-04. */
    amountRequired: 'Enter an amount received above zero before posting.',
    /** BR-FIN-03-02, customer half. */
    customerRequired: 'A customer payment must name a customer.',
    /** BR-FIN-03-02, other-income half. */
    creditAccountRequired: 'Other income must name an account to credit.',
    /** BR-FIN-03-04. */
    cashAccountsOnly: 'Received into must be one of the accounts inside Cash & Cash Equivalents.',
    /** BR-FIN-03-05. */
    creditAccountKind: 'An income receipt can never credit an asset or an expense account.',
    /**
     * Allocation targets: posted invoices of THIS customer. An invoice in
     * another currency is refused with `FX_REFUSALS.settleCurrencyMismatch`.
     */
    allocationInvoiceInvalid: 'Money can only be applied to posted invoices of this customer.',
    /** BR-FIN-03-17 — no route out of Posted. */
    noRouteOutOfPosted: 'A posted receipt is corrected by a reversing entry, never rewritten and never cancelled.',
    /** ERR-FIN-03-05. */
    noOpenInvoices: 'No open invoices for this customer.',
    /** BR-FIN-02-10 read across: only the posted state reverses. */
    reverseOnlyPosted: 'Only a posted receipt can be reversed. A draft is edited or deleted instead.',
    /** One reversal per voucher — FIN-02 §15's "no reversing a reversal" kept. */
    alreadyReversed: 'This receipt has already been reversed. Both entries remain in the books.',
    /** The GEN-72 shape applied to the reversing act: a correction carries its reason. */
    reverseReasonRequired: 'A posted receipt can only be reversed with a reason, which is written to the audit log.',
    /**
     * A cheque register record rides this voucher (Q-FIN-10-4: "the two screens
     * describe one event"), and the cheque lifecycle posts ledger legs of its
     * own — reversing the voucher underneath it would strand them. GEN-74's
     * principle: the message names the blocker.
     */
    chequeRides: (chequeCode) => `Cheque ${chequeCode} was recorded from this receipt. Correct it in the cheque register — reversing a bounced cheque undoes this voucher with it.`,
    /** BR-FIN-04-12 read across to the money-in side, date parameterised. */
    reconciledLocked: (statementDate) => `This receipt is reconciled on the ${statementDate} statement. Reopen the reconciliation before reversing it.`,
};
/**
 * D46 — FIN-03's two parameterised receipt refusals, structured (the money-in
 * mirror of PAYMENT_REFUSALS): `chequeRides` carries a cheque code,
 * `reconciledLocked` a statement date. Sentence parity pinned by
 * `refusal-census.spec.ts`.
 */
export const RECEIPT_REFUSALS = {
    chequeRides: (chequeCode) => ({
        message: RECEIPT_MESSAGES.chequeRides(chequeCode),
        key: 'receipts.errChequeRides',
        params: { chequeCode },
    }),
    reconciledLocked: (statementDate) => ({
        message: RECEIPT_MESSAGES.reconciledLocked(statementDate),
        key: 'receipts.errReconciledLocked',
        params: { statementDate },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const ReceiptStatusSchema = z.enum(['draft', 'posted', 'cancelled']);
export const ReceiptTypeSchema = z.enum(['customer', 'other_income']);
/**
 * The voucher HEAD's method — a derived summary since the tenders wave (S6):
 * the one tender's method, or `mixed` when the document carries more than
 * one. Never sent on a write; a write names its tenders (or, for callers
 * that predate the wave, one `method` from the five).
 */
export const PaymentMethodSchema = z.enum(['cash', 'card', 'bank_transfer', 'check', 'online', 'mixed']);
/** One row of the allocation grid: five derived facts + the one editable cell. */
export const AllocationRowSchema = z
    .object({
    invoiceId: z.uuid(),
    invoiceCode: z.string(),
    invoiceDate: IsoDateSchema,
    invoiceTotal: z.string(),
    /** POSTED receipts only (R9). Excludes this voucher while it is a draft. */
    alreadyPaid: z.string(),
    balanceDue: z.string(),
    /** The editable "Amount to apply". */
    amount: z.string(),
})
    .strict();
/** An open invoice offered by the picker (posted, balance above tolerance). */
export const OpenInvoiceSchema = z
    .object({
    invoiceId: z.uuid(),
    invoiceCode: z.string(),
    invoiceDate: IsoDateSchema,
    invoiceTotal: z.string(),
    alreadyPaid: z.string(),
    balanceDue: z.string(),
})
    .strict();
/**
 * BR-FIN-03-10 for a voucher that does not exist yet.
 *
 * The saved-voucher route walks the open set server-side; the NEW voucher had
 * no route to call, so the browser folded the whole open set itself. That
 * second implementation is what kept `GET /api/receipts/open-invoices`
 * unpaged — a page of the open set folds to a wrong total (PAGINATION.md §3
 * step 11) — so this request carries what the unsaved voucher knows (the
 * customer, and the amount as typed) and nothing else.
 */
export const AutoAllocatePreviewRequestSchema = z
    .object({
    partnerId: z.uuid(),
    /** The voucher's currency — absent = the customer's default. */
    currency: CurrencySchema.optional(),
    /**
     * Absent IS the zero-amount shortcut: "fill every invoice and write the
     * total back". The screen omits it when the box is blank or still being
     * typed, which is the same reading the browser fold gave those cases.
     */
    amount: MoneyStringSchema.optional(),
})
    .strict();
/** The spread, computed over the WHOLE open set — rows plus the figures. */
export const AutoAllocatePreviewSchema = z
    .object({
    /** Only the invoices that receive money, oldest first. */
    allocations: z.object({ invoiceId: z.uuid(), amount: MoneyStringSchema }).strict().array(),
    /** The sum of the rows above — the figure the browser used to fold for itself. */
    totalAllocated: MoneyStringSchema,
    /** The amount box after the walk; the written-back total on the shortcut. */
    amountReceived: MoneyStringSchema,
    /** BR-FIN-03-09 — the excess held as customer credit ("0.00" when none). */
    unallocated: MoneyStringSchema,
})
    .strict();
export const ReceiptSchema = z
    .object({
    id: z.uuid(),
    /** RV-#### (FIN-03 field 1). */
    code: z.string(),
    receiptNo: z.number().int().positive(),
    status: ReceiptStatusSchema,
    /** GEN-59: why the draft was voided — null until cancelled, and on rows cancelled before the reason existed. */
    cancelReason: z.string().nullable(),
    receiptDate: IsoDateSchema,
    receiptType: ReceiptTypeSchema,
    partnerId: z.uuid().nullable(),
    partnerCode: z.string().nullable(),
    partnerName: z.string().nullable(),
    /** The Cash & Cash Equivalents account debited on posting. */
    receivedIntoAccountId: z.uuid(),
    receivedIntoAccountCode: z.string(),
    method: PaymentMethodSchema,
    reference: z.string().nullable(),
    /** The total in the VOUCHER's currency — Σ tenders, each converted to it. */
    amount: z.string(),
    currency: z.string(),
    /** EC-19-10: the rate this document froze when it was confirmed, and its "as of" date. */
    ...frozenRateFields,
    /** S6 — one row per way the money came in (RV-02…RV-06). */
    tenders: ReceiptTenderSchema.array(),
    ...tenderSummaryFields,
    incomeCategory: z.string().nullable(),
    incomeDescription: z.string().nullable(),
    creditAccountId: z.uuid().nullable(),
    allocations: AllocationRowSchema.array(),
    /** Σ allocation amounts. */
    totalApplied: z.string(),
    /** BR-FIN-03-09 — amount − totalApplied: held as customer credit, never a block. */
    unallocated: z.string(),
    notes: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    postedAt: z.string().nullable(),
    /**
     * The reversal stamp (BR-FIN-03-17, R8). Set by the reverse route and by
     * a cheque bounce (FIN-10 §6) once EVERY tender on the voucher has
     * bounced. While set, the voucher's allocations no longer count toward
     * any "already paid" — the invoice balances are restored — and the
     * voucher stays posted, locked and visible.
     */
    reversedAt: z.string().nullable(),
    /**
     * FIN-10 §6 per tender: SOME of this voucher's cheques bounced and were
     * reversed, the rest still count. `amount` and the allocations are what
     * REMAINS; each bounced row says so on `tenders[].bouncedAt`. Never true
     * beside `reversedAt`.
     */
    partiallyReversed: z.boolean(),
    /** The opposite entry — one hop each way: this link here, `reversalOf` on the entry. */
    reversalJournalEntryId: z.uuid().nullable(),
    /** The entry's display code (JE-…), so the screen can name it without a second fetch. */
    reversalEntryCode: z.string().nullable(),
    /** Required by the reverse route; NULL on a bounce-stamped voucher. */
    reversalReason: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
const AllocationInputSchema = z
    .object({
    invoiceId: z.uuid(),
    amount: MoneyStringSchema,
})
    .strict();
/**
 * TWO SHAPES, ONE MEANING. A caller sends `tenders` (S6), or the pre-wave
 * trio `method + receivedIntoAccountId (+ amount)`, which the server folds
 * into ONE tender in the voucher's currency. Both are accepted so the
 * existing callers and tests keep working unchanged; sending both is refused
 * because two statements of the same money could disagree.
 */
export const CreateReceiptRequestSchema = z
    .object({
    receiptDate: IsoDateSchema,
    receiptType: ReceiptTypeSchema.default('customer'),
    partnerId: z.uuid().optional(),
    /**
     * The voucher's currency (owner decision, 30 Oct 2026) — absent = the customer's
     * default currency. A voucher settles only invoices in its OWN currency.
     */
    currency: CurrencySchema.optional(),
    /** Pre-wave shape: the one account. With `tenders`, each row names its own. */
    receivedIntoAccountId: z.uuid().optional(),
    method: ReceiptTenderMethodSchema.optional(),
    reference: z.string().trim().max(60).optional(),
    amount: MoneyStringSchema.optional(),
    /** S6 — the tender lines. */
    tenders: ReceiptTendersInputSchema.optional(),
    incomeCategory: z.string().trim().max(60).optional(),
    incomeDescription: z.string().trim().max(200).optional(),
    creditAccountId: z.uuid().optional(),
    allocations: AllocationInputSchema.array().optional(),
    notes: z.string().max(2000).optional(),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.tenders === undefined) {
        if (body.method === undefined) {
            ctx.addIssue({ code: 'custom', path: ['tenders'], message: TENDER_MESSAGES.atLeastOne });
        }
        if (body.receivedIntoAccountId === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['receivedIntoAccountId'],
                message: TENDER_MESSAGES.accountRequired,
            });
        }
    }
    else if (body.method !== undefined || body.amount !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['tenders'], message: TENDER_MESSAGES.notBoth });
    }
});
export const UpdateReceiptRequestSchema = z
    .object({
    receiptDate: IsoDateSchema.optional(),
    /** BR-FIN-03-03: changing the customer clears the whole allocation. */
    partnerId: z.uuid().optional(),
    /**
     * Absent = untouched (or the new customer's default when the customer
     * changes). A changed currency clears the whole allocation, as a changed
     * customer does — the rows were in the old currency.
     */
    currency: CurrencySchema.optional(),
    receivedIntoAccountId: z.uuid().optional(),
    method: ReceiptTenderMethodSchema.optional(),
    reference: z.string().trim().max(60).nullable().optional(),
    amount: MoneyStringSchema.optional(),
    /**
     * S6 — REPLACES the tender set. Absent, the pre-wave fields above patch
     * the voucher's single tender (and are ignored on a voucher that already
     * carries several — the grid is the only editor of a mixed document).
     */
    tenders: ReceiptTendersInputSchema.optional(),
    incomeCategory: z.string().trim().max(60).nullable().optional(),
    incomeDescription: z.string().trim().max(200).nullable().optional(),
    creditAccountId: z.uuid().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
export const SetAllocationsRequestSchema = z
    .object({
    allocations: AllocationInputSchema.array(),
})
    .strict();
export const ReverseReceiptRequestSchema = z
    .object({
    /** Required and audited — the GEN-72 shape for a correction to posted books. */
    reason: z.string().trim().min(1, RECEIPT_MESSAGES.reverseReasonRequired).max(500),
})
    .strict();
const receiptParams = z.object({ receiptId: z.uuid() }).strict();
export const receiptsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/receipts',
        query: pagedQuery({
            partnerId: z.uuid().optional(),
            status: ReceiptStatusSchema.optional(),
            /** RV-01 — the register's search box: receipt number, customer name, reference. */
            q: z.string().trim().max(120).optional(),
        }),
        responses: {
            200: pagedResponse('receipts', ReceiptSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Receipt vouchers (accounting.view), PAGED (NFR-42) — both filters in SQL.',
    },
    get: {
        method: 'GET',
        path: '/api/receipts/:receiptId',
        pathParams: receiptParams,
        responses: {
            200: ReceiptSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One voucher with its allocation grid (accounting.view).',
    },
    /**
     * PAGED (NFR-42), and it took two waves to get here safely.
     *
     * PAGINATION.md §10 called this route "blocked on a FOLD": the browser
     * walked the whole open set to answer Auto-allocate, so a page of it
     * printed a wrong TOTAL (§3 step 11). §11 moved the fold to
     * `autoAllocatePreview` below and then found the blocker underneath —
     * `allocationBody()` also rebuilt the SAVE payload from the visible rows,
     * and `setAllocations` REPLACES the collection, so a paged read would have
     * deleted every allocation off-screen (§3 step 13). Both are fixed before
     * this `pagedQuery` landed; the grid submits from its own state across
     * every page, never from these rows.
     *
     * The rows keep the `invoices` key they already had, so the addition is
     * additive on the wire (§3 step 1). The ORDER is oldest first with the
     * document number as the tiebreak — a date is shared by many invoices and
     * an ambiguous order under a LIMIT drops rows rather than reshuffling them.
     */
    openInvoices: {
        method: 'GET',
        path: '/api/receipts/open-invoices',
        // `currency`: the voucher's — absent = the customer's default. Only
        // invoices in that currency are open to it.
        query: pagedQuery({ partnerId: z.uuid(), currency: CurrencySchema.optional() }),
        responses: {
            200: pagedResponse('invoices', OpenInvoiceSchema),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: "The customer's open POSTED invoices for the allocation grid (accounting.view), oldest first, one page at a time — drafts never appear (R9, Q-FIN-03-4).",
    },
    create: {
        method: 'POST',
        path: '/api/receipts',
        body: CreateReceiptRequestSchema,
        responses: {
            201: ReceiptSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft voucher (accounting.create). Currency adopts the customer (BR-FIN-03-03).',
    },
    update: {
        method: 'PATCH',
        path: '/api/receipts/:receiptId',
        pathParams: receiptParams,
        body: UpdateReceiptRequestSchema,
        responses: {
            200: ReceiptSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT (accounting.edit). Changing the customer clears the allocation (BR-FIN-03-03); posted/cancelled answer 409 (BR-FIN-03-13).',
    },
    setAllocations: {
        method: 'PUT',
        path: '/api/receipts/:receiptId/allocations',
        pathParams: receiptParams,
        body: SetAllocationsRequestSchema,
        responses: {
            200: ReceiptSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Replace the allocation grid (accounting.edit). Refused when over-allocated by either BR-FIN-03-06/-07 test, half-cent tolerance.',
    },
    /**
     * The same walk as `autoAllocate`, for a voucher that has not been saved.
     * Computes and RETURNS; writes nothing, so it carries the read permission
     * rather than the write one and leaves no audit trail — there is no
     * document for a trail to be about.
     */
    autoAllocatePreview: {
        method: 'POST',
        path: '/api/receipts/auto-allocate-preview',
        body: AutoAllocatePreviewRequestSchema,
        responses: {
            200: AutoAllocatePreviewSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Oldest-first auto-allocate for an UNSAVED voucher (accounting.view, BR-FIN-03-10) — computed over the customer\'s WHOLE open set and persisted nowhere.',
    },
    autoAllocate: {
        method: 'POST',
        path: '/api/receipts/:receiptId/auto-allocate',
        pathParams: receiptParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: ReceiptSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Auto-allocate oldest-first (accounting.edit, BR-FIN-03-10); with a zero amount, fills every invoice and writes the total back.',
    },
    remove: {
        method: 'DELETE',
        path: '/api/receipts/:receiptId',
        pathParams: receiptParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (accounting.delete) — R8/BR-FIN-03-16; posted/cancelled answer 409.',
    },
    post: {
        method: 'POST',
        path: '/api/receipts/:receiptId/post',
        pathParams: receiptParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: ReceiptSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post receipt (accounting.approve) — Draft → Posted: writes the FIN-03 §12 entry (DR cash / CR receivables or income) in ONE transaction; the excess stays as customer credit (BR-FIN-03-09).',
    },
    cancel: {
        method: 'POST',
        path: '/api/receipts/:receiptId/cancel',
        pathParams: receiptParams,
        /** GEN-59: never empty — the reason is the act's price of admission. */
        body: z
            .object({
            reason: z.string().trim().min(1, RECEIPT_MESSAGES.cancelReasonRequired).max(500),
        })
            .strict(),
        responses: {
            200: ReceiptSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Void a DRAFT (accounting.edit) — Draft → Cancelled, the only transition beside posting (BR-FIN-03-17); a posted voucher answers 409. GEN-59: reason required, stored on the voucher and written to the audit row.',
    },
    reverse: {
        method: 'POST',
        path: '/api/receipts/:receiptId/reverse',
        pathParams: receiptParams,
        body: ReverseReceiptRequestSchema,
        responses: {
            200: ReceiptSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "Reverse a POSTED receipt (accounting.approve — reversing is a posting act, gated like Post): flips the voucher's own entry line by line (BR-FIN-02-09, exchange difference included), stamps reversed_at — which restores every allocated invoice's balance — and records the required reason (audited, receipt.reverse). Dated the company's today (GEN-73). One reversal ever (FIN-02 §15); refused while a cheque record or a completed reconciliation rides the voucher.",
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=receipts.js.map